import { useEffect, useState } from "react";
import { CalendarDays, MapPin, User } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, inr } from "@/lib/api";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Events() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-navy-base min-h-screen" data-testid="events-page">
      <SEO title="Workshops, Bootcamps & Events in Jorhat | XTM TECHNOLOGIES"
        description="AI workshops, Generative AI sessions, career seminars, summer bootcamps and institutional training events at XTM FutureSkills Academy, Jorhat." />
      <section className="relative hero-glow">
        <div className="absolute inset-0 cyber-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-2">Events & Workshops</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white" data-testid="events-heading">Learn Live at the Academy</h1>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
          {events.map((e) => (
            <div key={e.slug} className="bg-card border border-white/10 rounded-2xl p-6 flex flex-col hover:border-orange-500/40 transition-colors" data-testid={`event-${e.slug}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-bold uppercase tracking-wider text-orange-400 border border-orange-500/40 rounded-full px-2.5 py-1">{e.type}</span>
                <span className="text-xs font-semibold text-slate-300">{e.fee > 0 ? inr(e.fee) : "Free"}</span>
              </div>
              <h2 className="font-display text-lg font-semibold text-white mb-2">{e.title}</h2>
              <p className="text-sm text-slate-400 line-clamp-3 mb-5">{e.description}</p>
              <ul className="space-y-2 text-xs text-slate-400 mb-6">
                <li className="flex gap-2"><CalendarDays className="w-3.5 h-3.5 text-orange-400 shrink-0" /> {e.date} • {e.time}</li>
                <li className="flex gap-2"><MapPin className="w-3.5 h-3.5 text-orange-400 shrink-0" /> {e.venue}</li>
                <li className="flex gap-2"><User className="w-3.5 h-3.5 text-orange-400 shrink-0" /> {e.speaker} • {e.seats} seats</li>
              </ul>
              <div className="mt-auto">
                <EventRegisterDialog event={e} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function EventRegisterDialog({ event }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/events/register", { ...form, event_slug: event.slug });
      toast.success(data.message);
      setOpen(false);
      setForm({ name: "", phone: "", email: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl transition-colors" data-testid={`event-register-${event.slug}`}>
          Register
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-navy-surface border-white/10 text-slate-200 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-white">Register — {event.title}</DialogTitle>
          <p className="text-xs text-slate-400">{event.date} • {event.venue} • Register by {event.registration_deadline}</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="event-register-form">
          <div>
            <Label htmlFor="ev-name">Full Name *</Label>
            <Input id="ev-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="event-name-input" />
          </div>
          <div>
            <Label htmlFor="ev-phone">Phone *</Label>
            <Input id="ev-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="event-phone-input" />
          </div>
          <div>
            <Label htmlFor="ev-email">Email</Label>
            <Input id="ev-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="event-email-input" />
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold transition-colors" data-testid="event-register-submit">
            {submitting ? "Registering…" : "Confirm Registration"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
